/**
 * Wiki Markdown engine — ONE grammar for the read view and the editor.
 *
 * A faithful port of the Wiki prototype's own engine (`Inbox.dc.html`:
 * wkSplitMd / wkJoinMd / wkKind / wkBlockOf / wkToMd / wkMdBlocks / wkInline /
 * wkEsc / wkInlineHtml / wkMdToHtml / wkEmbedLabel / wkInlineMd / wkHtmlToMd).
 * Same block splitter, same kinds, same inline regex, same normalisations, so
 * `htmlToMd(mdToHtml(md))` returns byte-for-byte what the prototype returns
 * (pinned by `__tests__/wikiMarkdown.corpus.test.js`, whose expectations were
 * produced by running the prototype functions themselves).
 *
 * Deliberate departures, all for safety (none changes the round trip):
 * - links: only http(s), mailto and relative hrefs become links; any other
 *   scheme (javascript:, data:, vbscript:, ...) stays literal text.
 * - images: only http(s) URLs and `/api/wiki/assets/<id>` load; anything else
 *   stays literal text in the editor and shows the empty-image placeholder in
 *   the view.
 * - video embeds: the URL's real hostname must be YouTube, Loom or Vimeo
 *   (the prototype only searched the URL text, so `https://evil.example/?youtube.com`
 *   was an "embed"); otherwise the line is an ordinary paragraph.
 * - the inline italic rule `_x_` uses a capture instead of a regex lookbehind
 *   (same matches, no lookbehind syntax for older Safari).
 *
 * API
 *   splitMd(md)            -> string[]   raw block sources (CRLF normalised; [''] when empty)
 *   joinMd(parts)          -> string     blank line between blocks, single newline between list lines
 *   blockKind(src)         -> 'code'|'table'|'warn'|'note'|'quote'|'h1'|'h2'|'h3'|'divider'|'check'|'li'|'num'|'image'|'embed'|'p'
 *   parseBlock(src)        -> Block      one typed block (shapes below)
 *   parseMd(md)            -> Block[]    every non-blank block; `num` blocks carry `n` (1, 2, 3 per run)
 *   serializeBlocks(blocks)-> string     Block[] back to Markdown (prototype wkToMd)
 *   inlineTokens(text)     -> Token[]    {kind:'text'|'b'|'i'|'s'|'code'|'link', text, raw, href?, safeHref?}
 *   plainText(text)        -> string     inline marks stripped (prototype wkPlain)
 *   inlineHtml(text)       -> string     editor inline HTML (<b> <i> <s> <code> <a href>, \n -> <br>)
 *   mdToHtml(md)           -> string     editor HTML (contract in notes/FE-markdown.md)
 *   htmlToMd(htmlOrNode)   -> string     editor HTML (string or DOM node) back to Markdown
 *   escapeHtml(text)       -> string     & < > " escaped
 *   safeHref(href)         -> string|null
 *   safeImageSrc(url)      -> string|null
 *   embedProvider(url)     -> 'YouTube'|'Loom'|'Vimeo'|null
 *   embedLabel(url)        -> string     "▶ {Provider} video · {url}" (escaped)
 *   wikiAssetId(url)       -> string|null  id of a `/api/wiki/assets/<id>` URL
 *   slugify(text)          -> string
 *   looksLikeMarkdown(text)-> boolean    prototype paste sniff (convert via mdToHtml when true)
 *   countWords(text)       -> number     prototype word count
 *   splitImportTitle(raw, filename) -> {title, body, hasHeading}  prototype import title rule
 *   extractHeadings(md|blocks, {levels, idPrefix}) -> [{id, level, text, index}]
 *   headingIds(blocks, idPrefix) -> Map<blockIndex, id>
 *
 * Block shapes (`t` keeps the prototype names):
 *   {t:'h1'|'h2'|'h3', level, text}            ('###'..'######' -> h3)
 *   {t:'p', text}                               (text may contain '\n' line breaks)
 *   {t:'li', text} · {t:'num', text, n} · {t:'check', checked, text}
 *   {t:'quote', text} · {t:'callout', tone:'info'|'warn', marker, text}
 *   {t:'code', text} · {t:'divider'}
 *   {t:'table', head:string[], rows:string[][]}  (cells are plain text)
 *   {t:'image', url, text}                       (text = alt = caption)
 *   {t:'embed', url, provider}
 */

const FENCE_RE = /^\s*`{3}/;
const TABLE_RE = /^\s*\|/;
const QUOTE_RE = /^\s*>/;
const HEADING_LINE_RE = /^#{1,6}\s/;
const DIVIDER_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const LIST_LINE_RE = /^\s*([-*+]\s+|\d+[.)]\s+)/;
const IMAGE_LINE_RE = /^\s*!\[/;
const JOIN_LIST_RE = /^\s*([-*+]\s|\d+[.)]\s)/;
const WARN_RE = /^\s*>\s*\[!(WARNING|CAUTION|IMPORTANT)\]/i;
const NOTE_RE = /^\s*>\s*\[!(NOTE|TIP|INFO)\]/i;
const CHECK_RE = /^\s*[-*+]\s+\[( |x|X)\](\s|$)/;
const BULLET_RE = /^\s*[-*+]\s/;
const NUM_RE = /^\s*\d+[.)]\s/;
const IMAGE_BLOCK_RE = /^\s*!\[[^\]]*\]\([^)]*\)\s*$/;
const EMBED_LINE_RE = /^\s*https?:\/\/\S*(youtube\.com|youtu\.be|loom\.com|vimeo\.com)\S*\s*$/;
// Prototype inline grammar, first match wins, no nesting. The last alternative
// is the prototype's `(?:^|(?<=\s))_x_` rewritten as a capture: a match that
// starts with the whitespace character is shifted by one in inlineTokens().
const INLINE_RE = /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*|(?:^|\s)_[^_\s][^_]*_)/g;
const LINK_RE = /^\[([^\]]+)\]\(([^)\s]+)\)$/;
const SCHEME_RE = /^([a-z][a-z0-9+.-]*):/i;
// eslint-disable-next-line no-control-regex
const URL_NOISE_RE = /[\u0000-\u0020\u007f-\u009f]/g;
// eslint-disable-next-line no-control-regex
const HAS_URL_NOISE_RE = /[\u0000-\u0020\u007f-\u009f]/;
const ASSET_RE = /^\/api\/wiki\/assets\/([A-Za-z0-9_-][A-Za-z0-9._~-]*)(\?[A-Za-z0-9=&_.~%-]*)?$/;
const NBSP_RE = /\u00a0/g;
const ZWSP_RE = /\u200b/g;
const WORDS_RE = /[A-Za-z0-9\u00c0-\u024f]+/g;
const TRAILING_BLOCK_TYPES = ['table', 'divider', 'embed', 'code', 'image', 'li', 'num', 'check', 'quote', 'callout'];

/* ------------------------------------------------------------------ blocks */

export function splitMd(md) {
    const lines = String(md || '').replace(/\r\n?/g, '\n').split('\n');
    const out = [];
    let i = 0;
    let para = [];
    const flush = () => {
        if (para.length) { out.push(para.join('\n')); para = []; }
    };
    while (i < lines.length) {
        const l = lines[i];
        if (FENCE_RE.test(l)) {
            flush();
            const buf = [l];
            i += 1;
            while (i < lines.length && !FENCE_RE.test(lines[i])) { buf.push(lines[i]); i += 1; }
            if (i < lines.length) { buf.push(lines[i]); i += 1; }
            out.push(buf.join('\n'));
            continue;
        }
        if (TABLE_RE.test(l) || QUOTE_RE.test(l)) {
            const re = TABLE_RE.test(l) ? TABLE_RE : QUOTE_RE;
            flush();
            const buf = [];
            while (i < lines.length && re.test(lines[i])) { buf.push(lines[i]); i += 1; }
            out.push(buf.join('\n'));
            continue;
        }
        if (HEADING_LINE_RE.test(l) || DIVIDER_RE.test(l) || LIST_LINE_RE.test(l) || IMAGE_LINE_RE.test(l)) {
            flush();
            out.push(l);
            i += 1;
            continue;
        }
        if (!l.trim()) { flush(); i += 1; continue; }
        para.push(l);
        i += 1;
    }
    flush();
    return out.length ? out : [''];
}

export function joinMd(parts) {
    let s = '';
    parts.forEach((b, i) => {
        s += i === 0 ? b : ((JOIN_LIST_RE.test(parts[i - 1]) && JOIN_LIST_RE.test(b)) ? '\n' : '\n\n') + b;
    });
    return s;
}

export function blockKind(src) {
    const s = String(src || '');
    if (FENCE_RE.test(s)) return 'code';
    if (TABLE_RE.test(s)) return 'table';
    if (WARN_RE.test(s)) return 'warn';
    if (NOTE_RE.test(s)) return 'note';
    if (QUOTE_RE.test(s)) return 'quote';
    const m = s.match(/^(#{1,6})\s/);
    if (m) return m[1].length === 1 ? 'h1' : m[1].length === 2 ? 'h2' : 'h3';
    if (DIVIDER_RE.test(s)) return 'divider';
    if (CHECK_RE.test(s)) return 'check';
    if (BULLET_RE.test(s)) return 'li';
    if (NUM_RE.test(s)) return 'num';
    if (IMAGE_BLOCK_RE.test(s)) return 'image';
    if (EMBED_LINE_RE.test(s) && embedProvider(s.trim())) return 'embed';
    return 'p';
}

const unquote = s => s.split('\n').map(l => l.replace(/^\s*>\s?/, ''));

export function parseBlock(src) {
    const k = blockKind(src);
    const s = String(src || '');
    if (k === 'code') {
        const ls = s.split('\n');
        const closed = ls.length > 1 && FENCE_RE.test(ls[ls.length - 1]);
        return { t: 'code', text: ls.slice(1, closed ? -1 : undefined).join('\n') };
    }
    if (k === 'table') {
        const rows = s.split('\n')
            .map(l => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim()))
            .filter(r => !r.every(c => /^:?-{2,}:?$/.test(c) || c === ''));
        return {
            t: 'table',
            head: (rows[0] || []).map(c => plainText(c)),
            rows: rows.slice(1).map(r => r.map(c => plainText(c))),
        };
    }
    if (k === 'warn' || k === 'note') {
        const marker = (s.match(/^\s*>\s*\[!(\w+)\]/) || [])[1];
        return {
            t: 'callout',
            tone: k === 'warn' ? 'warn' : 'info',
            text: unquote(s).join(' ').replace(/^\[!\w+\]\s*/, ''),
            marker: marker ? marker.toUpperCase() : (k === 'warn' ? 'WARNING' : 'NOTE'),
        };
    }
    if (k === 'quote') return { t: 'quote', text: unquote(s).join(' ') };
    if (k === 'h1' || k === 'h2' || k === 'h3') return { t: k, text: s.replace(/^#{1,6}\s+/, ''), level: Number(k[1]) };
    if (k === 'divider') return { t: 'divider' };
    if (k === 'check') {
        const m = s.match(/^\s*[-*+]\s+\[( |x|X)\]\s*([\s\S]*)$/);
        return { t: 'check', checked: !!m && m[1] !== ' ', text: m ? m[2] : s };
    }
    if (k === 'li') return { t: 'li', text: s.replace(/^\s*[-*+]\s+/, '') };
    if (k === 'num') return { t: 'num', text: s.replace(/^\s*\d+[.)]\s+/, '') };
    if (k === 'image') {
        const m = s.match(/!\[([^\]]*)\]\(([^)\s]*)/);
        return { t: 'image', url: m ? m[2] : '', text: m ? m[1] : '' };
    }
    if (k === 'embed') {
        const url = s.trim();
        return { t: 'embed', url, provider: embedProvider(url) };
    }
    return { t: 'p', text: s };
}

export function parseMd(md) {
    let n = 0;
    return splitMd(md).filter(x => String(x).trim()).map((src) => {
        const b = parseBlock(src);
        n = b.t === 'num' ? n + 1 : 0;
        if (b.t === 'num') b.n = n;
        return b;
    });
}

export function serializeBlocks(blocks) {
    let n = 0;
    return joinMd((blocks || []).map((b) => {
        n = b.t === 'num' ? n + 1 : 0;
        const tx = b.text || '';
        if (b.t === 'h1') return '# ' + tx;
        if (b.t === 'h2') return '## ' + tx;
        if (b.t === 'h3') return '### ' + tx;
        if (b.t === 'li') return '- ' + tx;
        if (b.t === 'num') return n + '. ' + tx;
        if (b.t === 'check') return '- [' + (b.checked ? 'x' : ' ') + '] ' + tx;
        if (b.t === 'callout') return '> [!' + (b.tone === 'warn' ? 'WARNING' : 'NOTE') + '] ' + tx;
        if (b.t === 'quote') return '> ' + tx;
        if (b.t === 'divider') return '---';
        if (b.t === 'code') return '```\n' + tx + '\n```';
        if (b.t === 'table') {
            const head = b.head || [];
            return ['| ' + head.join(' | ') + ' |', '| ' + head.map(() => '---').join(' | ') + ' |']
                .concat((b.rows || []).map(r => '| ' + r.join(' | ') + ' |')).join('\n');
        }
        if (b.t === 'image') return '![' + tx + '](' + (b.url || '') + ')';
        if (b.t === 'embed') return b.url || '';
        return tx;
    }).filter(x => x !== ''));
}

/* ------------------------------------------------------------------ inline */

export function inlineTokens(s) {
    const out = [];
    const src = String(s == null ? '' : s);
    const re = new RegExp(INLINE_RE.source, 'g');
    let last = 0;
    let m;
    while ((m = re.exec(src))) {
        let tok = m[0];
        let index = m.index;
        if (/^\s/.test(tok)) { tok = tok.slice(1); index += 1; }
        if (index > last) out.push({ kind: 'text', text: src.slice(last, index), raw: src.slice(last, index) });
        const two = tok.slice(0, 2);
        if (two === '**' || two === '__') out.push({ kind: 'b', text: tok.slice(2, -2), raw: tok });
        else if (two === '~~') out.push({ kind: 's', text: tok.slice(2, -2), raw: tok });
        else if (tok[0] === '`') out.push({ kind: 'code', text: tok.slice(1, -1), raw: tok });
        else if (tok[0] === '[') {
            const mm = tok.match(LINK_RE);
            if (mm) out.push({ kind: 'link', text: mm[1], href: mm[2], safeHref: safeHref(mm[2]), raw: tok });
            else out.push({ kind: 'text', text: tok, raw: tok });
        } else out.push({ kind: 'i', text: tok.slice(1, -1), raw: tok });
        last = index + tok.length;
    }
    if (last < src.length) out.push({ kind: 'text', text: src.slice(last), raw: src.slice(last) });
    return out;
}

export function plainText(s) {
    return inlineTokens(s).map(r => r.text).join('');
}

export function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function inlineHtml(s) {
    return inlineTokens(s).map((r) => {
        const t = escapeHtml(r.text);
        if (r.kind === 'b') return '<b>' + t + '</b>';
        if (r.kind === 'i') return '<i>' + t + '</i>';
        if (r.kind === 's') return '<s>' + t + '</s>';
        if (r.kind === 'code') return '<code>' + t + '</code>';
        if (r.kind === 'link') {
            // Unsafe scheme: keep the Markdown source as literal text so the
            // round trip is unchanged and nothing clickable is produced.
            return r.safeHref ? '<a href="' + escapeHtml(r.safeHref) + '">' + t + '</a>' : escapeHtml(r.raw);
        }
        return t;
    }).join('').replace(/\n/g, '<br>');
}

/* ------------------------------------------------------------------ safety */

export function safeHref(href) {
    const raw = String(href == null ? '' : href);
    const probe = raw.replace(URL_NOISE_RE, '');
    if (!probe) return null;
    const m = probe.match(SCHEME_RE);
    if (m) {
        const scheme = m[1].toLowerCase();
        return scheme === 'http' || scheme === 'https' || scheme === 'mailto' ? raw : null;
    }
    // No scheme: a path, ?query, #hash or //host. A colon before the first
    // separator could still be read as a scheme by some parser — refuse it.
    const colon = probe.indexOf(':');
    const sep = probe.search(/[/?#]/);
    if (colon > -1 && (sep === -1 || colon < sep)) return null;
    return raw;
}

export function wikiAssetId(url) {
    const m = String(url == null ? '' : url).match(ASSET_RE);
    return m && !m[1].includes('..') ? m[1] : null;
}

export function safeImageSrc(url) {
    const s = String(url == null ? '' : url);
    if (/^https?:\/\//i.test(s) && !HAS_URL_NOISE_RE.test(s)) return s;
    return wikiAssetId(s) ? s : null;
}

export function embedProvider(url) {
    let host;
    try {
        const u = new URL(String(url == null ? '' : url).trim());
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
        host = u.hostname.toLowerCase();
    } catch {
        return null;
    }
    const on = domain => host === domain || host.endsWith('.' + domain);
    if (on('youtube.com') || host === 'youtu.be') return 'YouTube';
    if (on('loom.com')) return 'Loom';
    if (on('vimeo.com')) return 'Vimeo';
    return null;
}

export function embedLabel(url) {
    return '\u25b6 ' + (embedProvider(url) || 'Video') + ' video \u00b7 ' + escapeHtml(url);
}

/* ------------------------------------------------------- Markdown -> HTML */

export function mdToHtml(md) {
    const blocks = parseMd(md);
    let html = '';
    let open = null;
    const close = () => {
        if (open) { html += open === 'ol' ? '</ol>' : '</ul>'; open = null; }
    };
    blocks.forEach((b, idx) => {
        const want = b.t === 'li' ? 'ul' : b.t === 'num' ? 'ol' : b.t === 'check' ? 'task' : null;
        if (want !== open) {
            close();
            if (want) { html += want === 'task' ? '<ul data-task="1">' : '<' + want + '>'; open = want; }
        }
        const inl = inlineHtml(b.text || '') || '<br>';
        if (b.t === 'h1' || b.t === 'h2' || b.t === 'h3') {
            html += '<' + b.t + '>' + inl + '</' + b.t + '>';
            const nx = blocks[idx + 1];
            if (!nx || /^h[123]$/.test(nx.t)) html += '<p><br></p>';
        } else if (b.t === 'li' || b.t === 'num') html += '<li>' + inl + '</li>';
        else if (b.t === 'check') html += '<li data-checked="' + (b.checked ? 'true' : 'false') + '">' + inl + '</li>';
        else if (b.t === 'quote') html += '<blockquote>' + inl + '</blockquote>';
        else if (b.t === 'callout') html += '<blockquote data-callout="' + (b.tone === 'warn' ? 'warn' : 'note') + '">' + inl + '</blockquote>';
        else if (b.t === 'code') html += '<pre>' + (escapeHtml(b.text || '') || '<br>') + '</pre>';
        else if (b.t === 'divider') html += '<hr>';
        else if (b.t === 'table') {
            html += '<table><thead><tr>' + (b.head || []).map(h => '<th>' + (escapeHtml(h) || '<br>') + '</th>').join('')
                + '</tr></thead><tbody>'
                + (b.rows || []).map(r => '<tr>' + r.map(x => '<td>' + (escapeHtml(x) || '<br>') + '</td>').join('') + '</tr>').join('')
                + '</tbody></table>';
        } else if (b.t === 'image') {
            html += safeImageSrc(b.url)
                ? '<p><img src="' + escapeHtml(b.url) + '" alt="' + escapeHtml(b.text) + '"></p>'
                : '<p>' + escapeHtml('![' + (b.text || '') + '](' + (b.url || '') + ')') + '</p>';
        } else if (b.t === 'embed') html += '<div data-embed="' + escapeHtml(b.url) + '" contenteditable="false">' + embedLabel(b.url) + '</div>';
        else html += '<p>' + inl + '</p>';
    });
    close();
    const last = blocks.length ? blocks[blocks.length - 1].t : '';
    if (!blocks.length || TRAILING_BLOCK_TYPES.indexOf(last) > -1) html += '<p><br></p>';
    return html;
}

/* ------------------------------------------------------- HTML -> Markdown */

const clean = s => String(s || '').replace(NBSP_RE, ' ').replace(ZWSP_RE, '');
// Never content (pasted or parsed HTML): skipped instead of emitting their text.
const SKIP_TAGS = /^(script|style|template|noscript|title|head|meta|link)$/;
const isListTag = tag => tag === 'UL' || tag === 'OL';

function inlineMd(node) {
    let out = '';
    node.childNodes.forEach((n) => {
        if (n.nodeType === 3) { out += clean(n.nodeValue); return; }
        if (n.nodeType !== 1) return;
        const t = n.tagName.toLowerCase();
        if (SKIP_TAGS.test(t)) return;
        if (t === 'br') { out += '\n'; return; }
        if (t === 'img') { out += '![' + (n.getAttribute('alt') || '') + '](' + (n.getAttribute('src') || '') + ')'; return; }
        if (n.hasAttribute('data-embed')) { out += '\n' + n.getAttribute('data-embed') + '\n'; return; }
        if (t === 'code') { const tx = n.textContent.replace(ZWSP_RE, ''); out += tx ? '`' + tx + '`' : ''; return; }
        const inner = inlineMd(n);
        const mk = t === 'b' || t === 'strong' ? '**' : t === 'i' || t === 'em' ? '_' : t === 's' || t === 'strike' || t === 'del' ? '~~' : '';
        if ((!mk && t !== 'a') || !inner.trim()) { out += inner; return; }
        const lead = inner.match(/^\s*/)[0];
        const trail = inner.match(/\s*$/)[0];
        const core = inner.trim();
        out += lead + (t === 'a' ? '[' + core + '](' + (n.getAttribute('href') || '') + ')' : mk + core + mk) + trail;
    });
    return out;
}

function preText(el) {
    let s = '';
    el.childNodes.forEach((k) => {
        if (k.nodeType === 3) s += k.nodeValue;
        else if (k.nodeType === 1) {
            if (k.tagName === 'BR') s += '\n';
            else {
                if ((k.tagName === 'DIV' || k.tagName === 'P') && s && !/\n$/.test(s)) s += '\n';
                s += preText(k);
            }
        }
    });
    return s;
}

function toRoot(input) {
    if (input && typeof input === 'object' && input.nodeType === 9) return input.body;
    if (input && typeof input === 'object' && input.nodeType) return input;
    if (typeof DOMParser === 'undefined') throw new Error('htmlToMd needs a DOM (DOMParser) to parse HTML strings');
    // DOMParser documents are inert: no scripts run and no images load.
    return new DOMParser().parseFromString('<!doctype html><body>' + String(input == null ? '' : input), 'text/html').body;
}

export function htmlToMd(input) {
    const root = toRoot(input);
    const doc = root.ownerDocument || root;
    const out = [];
    const line = el => inlineMd(el).replace(/\n+/g, ' ').trim();
    const list = (el, ordered) => {
        // `data-type="taskList"` is TipTap's TaskList shape; the prototype's is `data-task="1"`.
        const task = el.getAttribute('data-task') === '1' || el.getAttribute('data-type') === 'taskList';
        let n = 0;
        [...el.children].forEach((li) => {
            if (li.tagName !== 'LI') { if (isListTag(li.tagName)) list(li, li.tagName === 'OL'); return; }
            const tmp = doc.createElement('div');
            [...li.childNodes].forEach((k) => { if (!(k.nodeType === 1 && isListTag(k.tagName))) tmp.appendChild(k.cloneNode(true)); });
            const nested = () => [...li.children].forEach((k) => { if (isListTag(k.tagName)) list(k, k.tagName === 'OL'); });
            if (!line(tmp) && !tmp.querySelector('img')) { nested(); return; }
            n += 1;
            out.push((task ? '- [' + (li.getAttribute('data-checked') === 'true' ? 'x' : ' ') + '] ' : ordered ? n + '. ' : '- ') + line(tmp));
            nested();
        });
    };
    const walk = (box) => {
        [...box.childNodes].forEach((n) => {
            if (n.nodeType === 3) { const t = clean(n.nodeValue).trim(); if (t) out.push(t); return; }
            if (n.nodeType !== 1) return;
            const t = n.tagName.toLowerCase();
            if (SKIP_TAGS.test(t)) return;
            if (n.hasAttribute('data-embed')) { out.push(n.getAttribute('data-embed')); return; }
            if (/^h[1-6]$/.test(t)) { const tx = line(n); if (tx) out.push('#'.repeat(Math.min(3, Number(t[1]))) + ' ' + tx); }
            else if (t === 'ul') list(n, false);
            else if (t === 'ol') list(n, true);
            else if (t === 'blockquote') {
                const tx = line(n);
                const c = n.getAttribute('data-callout');
                if (tx || c) out.push(c ? '> [!' + (c === 'warn' ? 'WARNING' : 'NOTE') + '] ' + tx : '> ' + tx);
            } else if (t === 'pre') out.push('```\n' + clean(preText(n)).replace(/\n+$/, '') + '\n```');
            else if (t === 'hr') out.push('---');
            else if (t === 'table') {
                const rows = [...n.querySelectorAll('tr')].map(tr => [...tr.children].map(cell => clean(cell.textContent).trim().replace(/\|/g, '/')));
                if (rows.length) {
                    out.push(['| ' + rows[0].join(' | ') + ' |', '| ' + rows[0].map(() => '---').join(' | ') + ' |']
                        .concat(rows.slice(1).map(r => '| ' + r.join(' | ') + ' |')).join('\n'));
                }
            } else if (t === 'img') {
                // A bare top-level <img> (TipTap block image). The prototype only ever
                // had images wrapped in <p>, which the generic branch below handles.
                out.push('![' + (n.getAttribute('alt') || '') + '](' + (n.getAttribute('src') || '') + ')');
            } else if ((t === 'div' || t === 'section') && n.querySelector('p,h1,h2,h3,h4,ul,ol,pre,blockquote,table,hr,[data-embed]')) walk(n);
            else {
                const tx = inlineMd(n).replace(/^\n+|\n+$/g, '').replace(/\n{2,}/g, '\n');
                if (tx.trim()) out.push(tx.trim());
            }
        });
    };
    walk(root);
    return joinMd(out);
}

/* ------------------------------------------------------------ editor aids */

/**
 * Prototype paste sniff (wkWysiPasteH): plain text that should be converted
 * through mdToHtml instead of inserted as text — multi-line, a block marker
 * at the start, or **bold** / [link](url) / `code` anywhere.
 */
export function looksLikeMarkdown(text) {
    const s = String(text == null ? '' : text);
    return /\n/.test(s.trim())
        || /^\s*(#{1,6}\s|[-*+]\s|\d+[.)]\s|>|`{3}|\|)/.test(s)
        || /\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|`[^`]+`/.test(s);
}

/** Prototype word count (editor header): runs of Latin letters and digits. */
export function countWords(text) {
    return (String(text == null ? '' : text).match(WORDS_RE) || []).length;
}

/**
 * Prototype import title rule (wkSetImport): a `# Title` at the very start of
 * the file (only leading whitespace before it) becomes the page title and is
 * removed from the body (plus the blank lines after it); otherwise the title
 * is the file name without its extension, `-`/`_` runs turned into spaces.
 * `body` is CRLF-normalised; `parseMd(body)` gives the preview blocks.
 */
export function splitImportTitle(raw, filename = '') {
    const text = String(raw || '').replace(/\r\n?/g, '\n');
    const m = text.match(/^\s*#\s+(.+)\n?/);
    return {
        title: m ? m[1].trim() : String(filename || '').replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
        body: m ? text.slice(m[0].length).replace(/^\n+/, '') : text,
        hasHeading: !!m,
    };
}

/* ---------------------------------------------------------------- headings */

export function slugify(text) {
    const s = String(text == null ? '' : text).toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}\s-]/gu, '').trim().replace(/[\s-]+/g, '-');
    return s || 'section';
}

export function headingIds(blocks, idPrefix = 'wk-') {
    const ids = new Map();
    const seen = new Map();
    (blocks || []).forEach((b, i) => {
        if (b.t !== 'h1' && b.t !== 'h2' && b.t !== 'h3') return;
        const base = idPrefix + slugify(plainText(b.text));
        const count = (seen.get(base) || 0) + 1;
        seen.set(base, count);
        ids.set(i, count === 1 ? base : base + '-' + count);
    });
    return ids;
}

/**
 * Headings for the page's "On this page" rail. Defaults to the prototype's
 * rail (`#` and `##` only, inline marks stripped). Ids match the ids
 * WikiMarkdownView renders for the same Markdown and prefix.
 */
export function extractHeadings(mdOrBlocks, { levels = [1, 2], idPrefix = 'wk-' } = {}) {
    const blocks = Array.isArray(mdOrBlocks) ? mdOrBlocks : parseMd(mdOrBlocks);
    const ids = headingIds(blocks, idPrefix);
    const out = [];
    blocks.forEach((b, i) => {
        if (!ids.has(i)) return;
        const level = Number(b.t[1]);
        if (levels.indexOf(level) === -1) return;
        out.push({ id: ids.get(i), level, text: plainText(b.text), index: i });
    });
    return out;
}
